import React, {useState, useContext, useEffect, useMemo, useCallback} from 'react';
import { translate } from 'react-multi-lang';
import { Row, Col, Button, Table, Modal, Tag, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { AllHtmlEntities } from 'html-entities';

import ContainerPagina from '../../../components/ContainerPagina';
import TitolPagina from '../../../components/TitolPagina';
import TriaCursEscolar from '../../../components/TriaCursEscolar';
import { Api } from '../../../helpers';
import { PerfilUsuariContext } from "../../../context/perfil-usuari";
import AlumnesClassesImportar from '../../../components/Formularis/perfils/admin/AlumnesClassesImportar';

const entities = new AllHtmlEntities();

function AlumnesClasses(props) {

  const [perfilUsuari] = useContext(PerfilUsuariContext);
  const [cursEscolarTriat, setCursEscolarTriat] = useState(perfilUsuari.curs_escolar_actual.id);
  const [refrescar, setRefrescar] = useState(0);

  const [classes, setClasses] = useState([]);
  const [relacions, setRelacions] = useState([]);
  const [mapCursos, setMapCursos] = useState({});
  const [mapAlumnes, setMapAlumnes] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalImportarVisible, setModalImportarVisible] = useState(false);

  // ---------- Càrrega de dades globals (cursos i alumnes) un sol cop ----------
  useEffect(() => {
    const carregarGlobals = async () => {
      try{
        const [resCursos, resAlumnes] = await Promise.all([
          Api.get('/cursos'),
          Api.get('/alumnes')
        ]);

        const mc = {};
        if(resCursos && resCursos.result && resCursos.result.success === 1 && resCursos.data && resCursos.data.cursos){
          resCursos.data.cursos.forEach((c) => { mc[Number(c.id)] = c; });
        }
        setMapCursos(mc);

        const ma = {};
        if(resAlumnes && resAlumnes.result && resAlumnes.result.success === 1 && resAlumnes.data && resAlumnes.data.alumnes){
          resAlumnes.data.alumnes.forEach((a) => { ma[Number(a.id)] = a; });
        }
        setMapAlumnes(ma);
      }catch(err){
        console.error('Error carregant cursos/alumnes:', err);
        message.error('Error carregant el catàleg de cursos i alumnes.');
      }
    };
    carregarGlobals();
  }, []);

  // ---------- Càrrega de classes + relacions per curs escolar ----------
  const carregarDades = useCallback(async () => {
    setLoading(true);
    try{
      const [resClasses, resRelacions] = await Promise.all([
        Api.get('/classes/all/' + cursEscolarTriat),
        Api.get('/cursescolar/alumnes_classes/' + cursEscolarTriat)
      ]);

      if(resClasses && resClasses.result && resClasses.result.success === 1 && resClasses.data && resClasses.data.classes){
        setClasses(resClasses.data.classes);
      }else{
        setClasses([]);
      }

      // Les relacions venen com a array directe dins data
      if(resRelacions && resRelacions.result && resRelacions.result.success === 1 && Array.isArray(resRelacions.data)){
        setRelacions(resRelacions.data);
      }else{
        setRelacions([]);
      }
    }catch(err){
      console.error('Error carregant classes/relacions:', err);
      message.error('Error carregant les classes i relacions.');
      setClasses([]);
      setRelacions([]);
    }finally{
      setLoading(false);
    }
  }, [cursEscolarTriat]);

  useEffect(() => {
    carregarDades();
  }, [carregarDades, refrescar]);

  // ---------- Accions UI ----------
  const triaCursChange = (curs) => {
    setCursEscolarTriat(curs);
    setRefrescar((r) => r + 1);
  };

  const tancarModal = () => {
    setModalImportarVisible(false);
    setRefrescar((r) => r + 1);
  };

  // ---------- Dades derivades ----------
  // Agrupar relacions per id_classe
  const relacionsPerClasse = useMemo(() => {
    const map = {};
    relacions.forEach((r) => {
      const key = Number(r.id_classe);
      if(!map[key]) map[key] = [];
      map[key].push(r);
    });
    return map;
  }, [relacions]);

  // Ordenar classes per ordre (camp del GET)
  const classesOrdenades = useMemo(() => {
    return [...classes].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
  }, [classes]);

  // ---------- Columnes taula principal (classes) ----------
  const columnsClasses = [
    { title: 'Id', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Classe', dataIndex: 'nom', key: 'nom' },
    {
      title: 'Curs',
      key: 'curs',
      render: (_, r) => {
        const c = mapCursos[Number(r.id_curs)];
        return c ? c.nom : (r.id_curs != null ? <Tag>{r.id_curs}</Tag> : '-');
      }
    },
    { title: 'Ordre', dataIndex: 'ordre', key: 'ordre', width: 80 },
  /*  {
      title: 'Ubicació',
      dataIndex: 'ubicacio',
      key: 'ubicacio',
      render: (v) => v || '-'
    },*/
    {
      title: 'Nº alumnes',
      key: 'nAlumnes',
      width: 110,
      align: 'center',
      render: (_, r) => {
        const n = (relacionsPerClasse[Number(r.id)] || []).length;
        return <Tag color={n > 0 ? 'blue' : 'default'}>{n}</Tag>;
      }
    }
  ];

  // ---------- Columnes sub-taula (alumnes d'una classe) ----------
  const columnsAlumnes = [
    { title: 'id_alumne', dataIndex: 'id_alumne', key: 'id_alumne', width: 100 },
    {
      title: 'Nom complet',
      key: 'nomComplet',
      render: (_, r) => {
        const a = mapAlumnes[Number(r.id_alumne)];
        if(!a) return <Tag color="default">#{r.id_alumne}</Tag>;
        const nomComplet = [
          a.nom || '',
          a.cognom1 || '',
          a.cognom2 || ''
        ].filter(Boolean).join(' ');
        // Format: cognom1 cognom2, nom
        //const cognoms = [a.cognom1 || '', a.cognom2 || ''].filter(Boolean).join(' ');
        //return cognoms ? `${cognoms}, ${a.nom || ''}` : (a.nom || '');
        return entities.decode(nomComplet);
      }
    },
    {
      title: 'Estat',
      key: 'estat',
      width: 110,
      render: (_, r) => {
        const a = mapAlumnes[Number(r.id_alumne)];
        const estat = a && a.estat;
        if(!estat) return '-';
        const colors = { actiu: 'green', reserva: 'orange', baixa: 'red' };
        return <Tag color={colors[estat] || 'default'}>{estat}</Tag>;
      }
    },
    {
      title: 'Curs',
      key: 'cursAlumne',
      render: (_, r) => {
        const c = mapCursos[Number(r.id_curs)];
        return c ? c.nom : (r.id_curs != null ? <Tag>{r.id_curs}</Tag> : '-');
      }
    },
    { title: 'id relació', dataIndex: 'id', key: 'idRelacio', width: 100 }
  ];

  // ---------- Render ----------
  return (
    <ContainerPagina xl={24}>
      <TitolPagina txt={props.t('alumnes_classes.titol') || 'Alumnes / Classes'} />
      <div style={{marginTop:'1rem', marginLeft:'3rem'}}>
        <Row gutter={16} style={{marginBottom: 16}} align="middle">
          <Col>
            <TriaCursEscolar
              defaultValue={cursEscolarTriat}
              onChange={(c) => triaCursChange(c)}
            />
          </Col>
          <Col>
            <Button
              icon={<UploadOutlined />}
              type="primary"
              onClick={() => setModalImportarVisible(true)}
            >
              {props.t('alumnes_classes.importar_excel') || 'Importar Excel'}
            </Button>
          </Col>
        </Row>

        <Table
          size="small"
          columns={columnsClasses}
          dataSource={classesOrdenades}
          rowKey="id"
          loading={loading}
          pagination={false}
          //pagination={{ pageSize: 25, position: ['topRight', 'bottomRight'] }}
          expandable={{
            expandedRowRender: (record) => {
              const rels = (relacionsPerClasse[Number(record.id)] || []).map((r) => ({ ...r, key: r.id }));
              if(rels.length === 0){
                return <span style={{color:'#888', fontStyle:'italic'}}>Sense alumnes en aquesta classe</span>;
              }
              return (
                <Table
                  size="small"
                  columns={columnsAlumnes}
                  dataSource={rels}
                  pagination={false}
                  rowKey="id"
                />
              );
            }
          }}
        />
      </div>

      <Modal
        title={props.t('alumnes_classes.importar_titol') || 'Importar alumnes/classes des d\'Excel'}
        visible={modalImportarVisible}
        onCancel={tancarModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <AlumnesClassesImportar
          cursEscolarTriat={cursEscolarTriat}
          callback={tancarModal}
        />
      </Modal>
    </ContainerPagina>
  );
}

export default translate(AlumnesClasses);
